"use client";

import * as React from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CameraPlusIcon } from "@/components/icons";
import { generatePassword } from "@/src/lib/generate-password";
import PasswordGeneratorField from "@/app/groups/components/StudentsTable/StudentDrawerContent/tabs/PasswordGeneratorField";
import {
  useUpdateUserMutation,
  useCreateUserMutation,
  useCreateTeacherMutation,
} from "@/api/user/userApi";
import { useUpdateMeMutation } from "@/api/auth/authPrivateApi";
import type { UpdateMePayload } from "@/api/auth/authPrivateApi";
import { User } from "@/types/user";

import UserProfileFields from "./UserProfileFields";
import AvatarUploadDialog from "./AvatarUploadDialog";
import {
  buildCreateStudentPayload,
  persistedUserTypeFromRole,
  type AuthType,
  type UserRole,
} from "./userProfilePayload";

export type { AuthType, UserRole };

type Props = {
  initialData?: Partial<User>;
  onSubmit?: (data: User) => void;
  showManagerRole?: boolean;
  mode?: "full" | "self-profile";
};

export const UserProfileDrawerContent = React.forwardRef<
  { handleSave: () => void },
  Props
>(({ initialData, onSubmit, showManagerRole = false, mode = "full" }, ref) => {
  const [formState, setFormState] = React.useState<User | null>(
    initialData as User
  );

  const [viewUserType, setViewUserType] = React.useState<UserRole>("student");
  const selfProfileSnapshotRef = React.useRef<Record<string, unknown> | null>(null);

  const [avatarDialogOpen, setAvatarDialogOpen] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState("ru");

  const t = useTranslations();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [updateMe, { isLoading: isUpdatingMe }] = useUpdateMeMutation();
  const [createUser, { isLoading: isCreatingStudent }] =
    useCreateUserMutation();
  const [createTeacher, { isLoading: isCreatingTeacher }] =
    useCreateTeacherMutation();

  React.useEffect(() => {
    const data = (initialData ?? {}) as User;
    const group = (data as any)?.group as string | undefined;

    if (mode === "self-profile") {
      selfProfileSnapshotRef.current = (initialData ?? {}) as Record<string, unknown>;
    } else {
      selfProfileSnapshotRef.current = null;
    }

    const initialUserType = data.user_type as UserRole | undefined;

    const resolvedUserType: UserRole =
      initialUserType === "manager" && !showManagerRole
        ? "teacher"
        : initialUserType ??
        (group === "manager"
          ? showManagerRole
            ? "manager"
            : "teacher"
          : group === "teacher" || group === "reviewer"
            ? "teacher"
            : "student");

    setViewUserType(resolvedUserType);

    // Generate password if not present
    const password = data.password || generatePassword();

    setFormState({
      ...data,
      password,
      ...(mode === "self-profile"
        ? {}
        : {
          user_type: persistedUserTypeFromRole(resolvedUserType),
          ...(resolvedUserType === "manager" ? { group: "manager" } : {}),
        }),
      auth_type: (data.auth_type as AuthType | undefined) ?? "password",
    });

    setSelectedLanguage((data as any)?.additional?.language ?? "ru");
    setShowErrors(false);
  }, [initialData, showManagerRole, mode]);

  const user = formState;

  if (!user) {
    return <div>{t("label-loading")}</div>;
  }

  const initials =
    (user.firstname?.[0] ?? "") + (user.lastname?.[0] ?? "");

  const update = (next: User) => setFormState(next);

  const handleSave = async () => {
    if (!formState) return;

    setShowErrors(true);

    const firstname = (formState.firstname ?? "").toString().trim();
    const lastname = (formState.lastname ?? "").toString().trim();

    if (!firstname || !lastname) {
      toast.error(t("error-fill-required-fields"));
      return;
    }

    // --- self profile mode: PUT /user.json (only firstname/lastname/photo) ---
    if (mode === "self-profile") {
      try {
        const current = formState as any;
        const snap = (selfProfileSnapshotRef.current ?? {}) as any;

        const payload: UpdateMePayload = {
          id: Number(snap.id ?? current.id),
          school_id: snap.school_id ?? current.school_id ?? null,
          auth_type: null,
          group: null,
          photo: current.photo ?? snap.photo ?? null,
          photo_thumb: snap.photo_thumb ?? current.photo_thumb ?? {},
          color: snap.color ?? current.color ?? null,
          firstname,
          lastname,
          patronymic: null,
          email: snap.email ?? current.email ?? null,
          phone: snap.phone ?? current.phone ?? null,
          username: snap.username ?? current.username ?? null,
          description: snap.description ?? current.description ?? null,
          password: (formState as any)._passwordLocked === false ? formState.password ?? null : null,
          school: snap.school ?? current.school ?? {},
          register_date: snap.register_date ?? current.register_date ?? null,
          last_activity_date:
            snap.last_activity_date ?? current.last_activity_date ?? null,
          additional: { ...(snap.additional ?? current.additional ?? {}), language: selectedLanguage },
          is_online: Boolean(snap.is_online ?? current.is_online ?? false),
          is_need_complete_challenge: Boolean(
            snap.is_need_complete_challenge ??
            current.is_need_complete_challenge ??
            false
          ),
          is_multiple_schools: Boolean(
            snap.is_multiple_schools ?? current.is_multiple_schools ?? false
          ),
        };

        const updated = await updateMe(payload).unwrap();
        onSubmit?.(updated as any);
        toast.success(t("ui-notification-changes-saved"));
      } catch (err: unknown) {
        console.error("[UserProfileDrawerContent:self-profile] Failed to update me", err);
        toast.error(t("ui-error-create-user"));
      }
      return;
    }

    // --- full editor mode ---
    const roleForSave = viewUserType;
    const requiredAll = roleForSave === "teacher" || roleForSave === "manager";

    const patronymic = (formState.patronymic ?? "").toString().trim();
    const phone = (formState.phone ?? "").toString().trim();

    const rawEmail = (formState.email ?? "").toString().trim();
    const normalizedEmail = rawEmail.length ? rawEmail : null;

    if (requiredAll) {
      if (!patronymic || !phone || !normalizedEmail) {
        toast.error(t("error-fill-required-fields"));
        return;
      }
    }

    if (normalizedEmail) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
      if (!isValidEmail) {
        toast.error(t("error-email-format"));
        return;
      }
    }

    const fullname = [firstname, lastname, patronymic].filter(Boolean).join("_").toLowerCase();
    const username = fullname.padEnd(12, "_");

    const normalizedFormState = {
      ...formState,
      firstname,
      lastname,
      patronymic: patronymic || null,
      phone: phone || null,
      email: normalizedEmail,
      username,
      password: (formState as any)._passwordLocked === false ? formState.password : null,
      fullname,
      additional: { ...((formState as any)?.additional ?? {}), language: selectedLanguage },
    } as User;

    const isNew = !normalizedFormState.id;

    try {
      let result: User | null = null;

      if (normalizedFormState.id) {
        // Strip frontend-only flags
        const { _passwordLocked, ...payload } = { ...normalizedFormState } as any;

        if (roleForSave === "manager") {
          (payload as any).group = "manager";
        } else if (roleForSave === "teacher") {
          (payload as any).group = "teacher";
        } else if (roleForSave === "student") {
          (payload as any).group = "student";
        }

        result = (await updateUser(payload).unwrap()) as User;
      } else {
        const createPayload = buildCreateStudentPayload(normalizedFormState);

        if (roleForSave === "manager") {
          (createPayload as any).user = {
            ...(createPayload as any).user,
            group: "manager",
          };
        }

        const isTeacherLike = roleForSave === "teacher" || roleForSave === "manager";

        const created = isTeacherLike
          ? await createTeacher(createPayload as any).unwrap()
          : await createUser(createPayload as any).unwrap();

        result = created as User;
      }

      if (result && onSubmit) {
        onSubmit(result);
      }

      if (isNew) {
        toast.success(t("ui-notification-user-created"));
      } else {
        toast.success(t("ui-notification-changes-saved"));
      }
    } catch (err: unknown) {
      const anyErr = err as any;
      console.error("Failed to save user (raw):", anyErr);
      toast.error(t("ui-error-create-user"));
    }
  };

  React.useImperativeHandle(ref, () => ({
    handleSave,
  }));

  const isCreating = isCreatingStudent || isCreatingTeacher || isUpdatingMe;

  return (
    <div className="flex gap-6 rounded-lg border p-6 shadow-sm text-sm leading-relaxed">
      {/* Left side - Avatar */}
      <div className="flex flex-col items-center gap-2 shrink-0">
        <div className="relative">
          <Avatar className="w-24 h-24 object-cover rounded-lg">
            <AvatarImage src={user.photo ?? undefined} />
            <AvatarFallback className="text-lg">{initials || "?"}</AvatarFallback>
          </Avatar>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="absolute -bottom-1 -end-1 h-8 w-8 rounded-full bg-white shadow-md border"
            onClick={() => setAvatarDialogOpen(true)}
          >
            <CameraPlusIcon size={16} />
          </Button>
        </div>
      </div>

      {/* Right side - form fields */}
      <div className="flex-1 flex flex-col gap-6">
        <UserProfileFields
          user={user}
          onChange={update}
          showErrors={showErrors}
          selectedLanguage={selectedLanguage}
          onLanguageChange={setSelectedLanguage}
        />

        <PasswordGeneratorField
          value={formState}
          onChange={(next) => setFormState(next as User)}
        />

        {/* Hidden Save Button (triggered via ref) */}
        <div className="hidden">
          <Button onClick={handleSave} disabled={isUpdating || isCreating}>
            {isUpdating || isCreating ? t("ui-saving") : t("btn-save")}
          </Button>
        </div>
      </div>

      <AvatarUploadDialog
        open={avatarDialogOpen}
        onOpenChange={setAvatarDialogOpen}
        onPhotoSelected={(src) =>
          setFormState((prev) => (prev ? { ...prev, photo: src } : prev))
        }
      />
    </div>
  );
});

UserProfileDrawerContent.displayName = "UserProfileDrawerContent";

export default UserProfileDrawerContent;
